/** Names the shape editors offer in pickers: part paths, joints, materials. */

import { partRows, type ModelDoc, type ModelShape } from '../../../lib/model-doc.js';

export interface ShapeEditorProps {
  shape: ModelShape;
  /** Replaces the part's shape (one undo step). */
  onChange: (shape: ModelShape) => void;
  doc: ModelDoc;
  /** Prism path of the part being edited (excluded from part pickers). */
  partPath: string;
}

/** Every part path of the doc except `exclude` (and its children). */
export function partPaths(doc: ModelDoc, exclude?: string): string[] {
  return partRows(doc)
    .map((r) => r.partPath)
    .filter((p) => !exclude || (p !== exclude && !p.startsWith(`${exclude}/`)));
}

const SIDE = /([._])([LR])$/;

/** Names a skin node gets after `symmetry` (prism skin: `.R` positive side, `.L` mirrored). */
export function mirroredNodeNames(name: string, offPlane: boolean): string[] {
  if (!offPlane) return [name];
  const m = SIDE.exec(name);
  if (m) return [name, name.replace(SIDE, `${m[1]}${m[2] === 'L' ? 'R' : 'L'}`)];
  return [`${name}.R`, `${name}.L`];
}

const AXIS_INDEX: Record<string, number> = { x: 0, y: 1, z: 2 };

/** Joint names of a skin shape (with its symmetry twins). */
export function skinJointNames(shape: ModelShape): string[] {
  const nodes = Array.isArray(shape.nodes) ? (shape.nodes as { name?: unknown; position?: unknown }[]) : [];
  const axis = typeof shape.symmetry === 'string' ? AXIS_INDEX[shape.symmetry.toLowerCase()] : undefined;
  return nodes.flatMap((n) => {
    const name = typeof n.name === 'string' ? n.name : '';
    if (!name) return [];
    const pos = Array.isArray(n.position) ? (n.position as number[]) : [];
    const off = axis !== undefined && Math.abs(pos[axis] ?? 0) > 1e-4;
    return mirroredNodeNames(name, off);
  });
}

/** Joints the model has: `doc.skeleton` plus every skin part's nodes. */
export function boneNames(doc: ModelDoc): string[] {
  const out = new Set<string>();
  for (const b of Array.isArray(doc.skeleton) ? (doc.skeleton as { name?: unknown }[]) : []) {
    if (typeof b.name === 'string' && b.name) out.add(b.name);
  }
  for (const row of partRows(doc)) {
    const shape = row.part.shape;
    if (shape && (shape.type === 'skin' || shape.type === 'skeleton')) skinJointNames(shape).forEach((n) => out.add(n));
  }
  return [...out];
}

export function materialNames(doc: ModelDoc): string[] {
  return ['default', ...Object.keys(doc.materials ?? {}).filter((m) => m !== 'default')];
}
